import React, { useCallback, useEffect, useState, useContext } from 'react';
import { FBMOrdersV2Api } from '../../../../api/fbmOrdersV2';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Tooltip,
    IconButton,
} from '@mui/material';
import { UserContext } from '../../../../contexts/UserContext';
import { useGetTableData } from '../../hooks/useGetTableData';
import { InfoOutlined } from '@material-ui/icons';
import { FBMOrdersV2Helpers } from './FBMOrdersV2Helpers';

const ChangeFBMShipmentLineHaulModal = ({ isOpen, onClose, onSuccess, onError, shipmentData }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [selectedLineHaulId, setSelectedLineHaulId] = useState('');
    const [filteredLineHauls, setFilteredLineHauls] = useState([]);
    const { fbmAccount, user } = useContext(UserContext);
    const fbmOrdersV2Api = new FBMOrdersV2Api();
    const fbmOrdersV2Helpers = new FBMOrdersV2Helpers();

    const fetchLineHauls = useCallback(() => {
        // Only Fedex for now
        return fbmOrdersV2Api.getLineHauls(fbmAccount.fbmAccountName, shipmentData?.facilityId, 'fedex');
    }, [fbmAccount.fbmAccountName, shipmentData?.facilityId]);

    const { data: lineHauls, isLoading: lineHaulsLoading, error: lineHaulsError } = useGetTableData(fetchLineHauls);

    useEffect(() => {
        if (lineHauls) {
            setFilteredLineHauls(fbmOrdersV2Helpers.filterLineHaulsForChangeLineHaulModals(lineHauls));
        }
    }, [lineHauls]);

    useEffect(() => {
        if (!(onSuccess instanceof Function)) {
            console.error('ChangeFBMShipmentLineHaulModal intialized incorrectly onSuccess is not a function');
        }
        if (!(onError instanceof Function)) {
            console.error('ChangeFBMShipmentLineHaulModal intialized incorrectly onError is not a function');
        }
        if (!(onClose instanceof Function)) {
            console.error('ChangeFBMShipmentLineHaulModal intialized incorrectly onClose is not a function');
        }
    }, [onClose, onSuccess, onError]);

    const handleClose = () => {
        onClose();
    };

    const handleChangeLineHaul = useCallback(async () => {
        setIsLoading(true);
        const response = await fbmOrdersV2Api.changeLineHaul(
            shipmentData.shipmentId,
            fbmAccount.fbmAccountName,
            selectedLineHaulId
        );
        if (response.status) {
            onSuccess(`Shipment ${shipmentData.shipmentId} line haul changed successfully`);
            handleClose();
        } else {
            onError(response.msg || 'Failed to change line haul');
        }
        setIsLoading(false);
    }, [
        fbmAccount.fbmAccountName,
        fbmOrdersV2Api,
        handleClose,
        onError,
        onSuccess,
        shipmentData?.shipmentId,
        selectedLineHaulId,
        shipmentData?.facilityId,
    ]);

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            disableScrollLock={true}
            hideBackdrop={false}
        >
            <DialogTitle>Change Line Haul</DialogTitle>
            <DialogContent>
                {shipmentData && (
                    <div style={{ marginBottom: '20px' }}>
                        <Typography variant="subtitle1">
                            <strong>Shipment ID:</strong> {shipmentData.shipmentId}
                        </Typography>
                        <Typography variant="subtitle1">
                            <strong>Carrier:</strong> {shipmentData.carrier}
                        </Typography>
                        <Typography variant="subtitle1">
                            <strong>Status:</strong> {shipmentData.status}
                        </Typography>
                    </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <InputLabel>Line Haul</InputLabel>
                    <Tooltip title="Format: Carrier - Hub - Arrival Day" placement="right" arrow>
                        <IconButton size="small">
                            <InfoOutlined fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </div>
                <FormControl fullWidth sx={{ mb: 2 }}>
                    <Select
                        value={selectedLineHaulId}
                        onChange={(e) => setSelectedLineHaulId(e.target.value)}
                        label="Line Haul"
                    >
                        {Array.isArray(filteredLineHauls) &&
                            filteredLineHauls.map((lineHaul) => (
                                <MenuItem key={lineHaul.id} value={lineHaul.id}>
                                    {lineHaul.carrier} - {lineHaul.hub} - {lineHaul.arrivalDay}
                                </MenuItem>
                            ))}
                    </Select>
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="secondary">
                    Cancel
                </Button>
                <Button onClick={handleChangeLineHaul} color="primary" variant="contained" disabled={isLoading}>
                    {isLoading ? 'Changing...' : 'Confirm Line Haul Change'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export { ChangeFBMShipmentLineHaulModal };
